import 'moment/locale/it';
import '../../css/tempo.scss';
import { prepareDaysData } from '../../utils/Utility';
import PropTypes from 'prop-types';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableFooter from '@mui/material/TableFooter';
import Paper from '@mui/material/Paper';
import { WorklogDayCell } from './WorklogDayCell';
import { WorklogIssueCell } from './WorklogIssueCell';
import cn from 'classnames';
import { decimalFormatter } from '../../components/Table/utility';
import { forwardRef, useEffect, useState, useCallback, useRef } from 'react';
import { DayHeaderCell } from '../../components/Cells/CalendarTable/DayHeaderCell';
import TableCell from '@mui/material/TableCell';
import { getWorklogTotalHours } from '../../utils/API/worklog';
import { isValidWorklog } from '../../utils/worklogValidation';

const WorklogTable = forwardRef((props, ref) => {
  const {
    allIssues,
    userProjects,
    worklogs,
    dayHeaders,
    isReadOnly,
    onDeleteClick,
    onEditClick,
    onScroll,
    onUpdate,
    username,
    onWorklogChange,
  } = props;

  const [totalTableHours, setTotalTableHours] = useState({});
  const [updateCounter, setUpdateCounter] = useState(0);
  const [localWorklogs, setLocalWorklogs] = useState();
  const [data, setData] = useState([]);
  const isMountedRef = useRef(true);

  const totalHoursPerAllIssues = data.reduce(
    (acc, curr) => acc + curr.hours,
    0
  );

  useEffect(() => {
    async function updateTotals() {
      if (!dayHeaders?.length || !username) {
        return;
      }
      const workedDays = await getWorklogTotalHours(
        dayHeaders[0].day.format('MM'),
        dayHeaders[0].day.format('YYYY'),
        username
      );

      const mergedDays = dayHeaders.map((header) => {
        const date = header.day.format('YYYY-MM-DD');
        const workedDay = workedDays.month?.find((day) => day.date === date);

        return {
          date,
          time_spent: workedDay ? workedDay.time_spent : 0,
        };
      });

      if (isMountedRef.current) {
        setTotalTableHours({
          total_time_spent:
            workedDays.total_time_spent ??
            decimalFormatter.format(totalHoursPerAllIssues),
          month: mergedDays,
        });

        onUpdate((prev) => ({
          ...prev,
          total_time_spent:
            workedDays.total_time_spent ??
            decimalFormatter.format(totalHoursPerAllIssues),
        }));
      }
    }

    updateTotals();
  }, [dayHeaders, username, updateCounter, onUpdate, totalHoursPerAllIssues]);

  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    []
  );

  useEffect(() => {
    setLocalWorklogs(worklogs);
  }, [worklogs]);

  useEffect(() => {
    setData(
      prepareDaysData({
        worklogs: localWorklogs || [],
        allIssues: allIssues || [],
        userProjects: userProjects || [],
      })
    );
  }, [localWorklogs, allIssues, userProjects]);

  const handleUpdateWorklog = useCallback(
    (newWorklog) => {
      if (!newWorklog || !isValidWorklog(newWorklog)) {
        console.error('Invalid worklog structure:', newWorklog);
        return;
      }

      setLocalWorklogs((prevWorklogs) => {
        const existingWorklog = prevWorklogs.find(
          (wl) => wl.id === newWorklog.id
        );
        if (existingWorklog) {
          return prevWorklogs.map((worklog) =>
            worklog.id === newWorklog.id
              ? { ...worklog, ...newWorklog }
              : worklog
          );
        }
        return [...prevWorklogs, { ...newWorklog }];
      });
      setUpdateCounter((prev) => prev + 1);
      if (onWorklogChange) {
        onWorklogChange();
      }
    },
    [onWorklogChange]
  );

  const handleDeleteWorklog = useCallback(
    (worklogId) => {
      setLocalWorklogs((prevWorklogs) =>
        prevWorklogs.filter((wl) => wl.id !== worklogId)
      );
      setUpdateCounter((prev) => prev + 1);
      if (onWorklogChange) {
        onWorklogChange();
      }
    },
    [onWorklogChange]
  );

  const setIsActive = (worklog) => {
    if (isReadOnly) return true;
    return !worklog?.active;
  };

  return (
    <TableContainer component={Paper} onScroll={onScroll} ref={ref}>
      <Table stickyHeader className="month-table">
        <TableHead>
          <TableRow>
            <TableCell>Attività</TableCell>
            <TableCell>Ore</TableCell>
            {dayHeaders.map((header) => (
              <DayHeaderCell
                day={header.day}
                isWeekEnd={header.weekEnd}
                isHoliday={header.isHolidays}
                isToday={header.isToday}
                key={header.day.format('DD')}
              />
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((item) => (
            <TableRow key={item.issueId}>
              <WorklogIssueCell
                projectSummary={item.projectSummary}
                issueSummary={item.issueSummary}
              />
              <TableCell>
                {decimalFormatter.format(Number(item.hours))}
              </TableCell>
              {dayHeaders.map((header) => {
                const key = header.day.format('DD');
                const worklog = item.days[key];
                return (
                  <WorklogDayCell
                    worklog={worklog}
                    date={header.day.format('YYYY-MM-DD')}
                    issueId={item.issueId}
                    isReadOnly={setIsActive(item)}
                    isWeekEnd={header.weekEnd}
                    isHoliday={header.isHolidays}
                    isToday={header.isToday}
                    onDeleteClick={onDeleteClick}
                    onEditClick={onEditClick}
                    key={key}
                    onDeleteCell={handleDeleteWorklog}
                    onUpdateCell={handleUpdateWorklog}
                  />
                );
              })}
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableCell>Totale</TableCell>
            <TableCell>
              {decimalFormatter.format(
                Number(totalTableHours.total_time_spent)
              )}
            </TableCell>
            {totalTableHours.month?.map((day) => {
              const key = day.date;
              const tableCellClassNames = cn({
                today: day.date === new Date().toISOString().slice(0, 10),
              });

              return (
                <TableCell key={key} className={tableCellClassNames}>
                  {day.time_spent > 0
                    ? decimalFormatter.format(Number(day.time_spent))
                    : ''}
                </TableCell>
              );
            })}
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
});

WorklogTable.defaultProps = {
  isReadOnly: false,
  dayHeaders: [],
  allIssues: [],
  userProjects: [],
  worklogs: [],
  onDeleteClick: () => {},
  onEditClick: () => {},
  onScroll: () => {},
  onUpdate: () => {},
  username: null,
  onWorklogChange: () => {},
};

WorklogTable.propTypes = {
  isReadOnly: PropTypes.bool,
  dayHeaders: PropTypes.instanceOf(Array),
  allIssues: PropTypes.instanceOf(Array),
  userProjects: PropTypes.instanceOf(Array),
  worklogs: PropTypes.instanceOf(Array),
  onDeleteClick: PropTypes.func,
  onEditClick: PropTypes.func,
  onScroll: PropTypes.func,
  onUpdate: PropTypes.func,
  username: PropTypes.string,
  onWorklogChange: PropTypes.func,
};

WorklogTable.displayName = 'WorklogTable';
export default WorklogTable;
